import React, { useState, useEffect, useRef } from "react";
import { Table, Form, Button } from "react-bootstrap";
import ExportExcel from "../../utility/ExportCSV";
import API_BASE_URL from "../../config";
import { authFetch } from "../../utility/authFetch";
import { useNavigate } from "react-router-dom";

function DailyCollectionReportTable({
  fromDate,
  toDate,
  setLoader,
  setButtonClicked,
}) {
  const [entries, setEntries] = useState([]);
  const tableRef = useRef();
  const navigate = useNavigate();

  const printPrescription = async (patientId) => {
    const res = await authFetch(
      `${API_BASE_URL}/print_sheet?patientId=${patientId}`
    );
    const data = await res.json();

    const imgSrc = "data:image/png;base64," + data.image;

    // Create a printable window
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
    <html>
      <head>
        <title>Print Sheet</title>
        <style>
          body, html { margin: 0; padding: 0; }
          img { width: 100%; }
        </style>
      </head>
      <body>
        <img src="${imgSrc}" />
      </body>
    </html>
  `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.print();
      // Optionally close automatically
      printWindow.close();
    };
  };

  const editDetails = (data) => {
    navigate("/edit-patient-details", {
      replace: false,
      state: { data: data },
    });
  };

  useEffect(() => {
    if (
      fromDate === "" ||
      toDate === "" ||
      fromDate === null ||
      toDate === null
    )
      return;
    if (!setButtonClicked) return;
    authFetch(
      `${API_BASE_URL}/api/daily-collection-report?fromDate=${fromDate}&toDate=${toDate}`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          alert("Error fetching data");
          setButtonClicked(false);
          setLoader(false);
          return;
        }
        setEntries(data.report);
        setButtonClicked(false);
        setLoader(false);
      })
      .catch((err) => {
        console.error("Error fetching entries:", err);
        setButtonClicked(false);
        setLoader(false);
        return;
      });
  }, [fromDate, toDate]);

  const totalRegistration = entries.reduce(
    (sum, item) => sum + (Number(item.registrationFee) || 0),
    0
  );

  const totalTestFee = entries.reduce(
    (sum, item) => sum + (Number(item.testFee) || 0),
    0
  );

  const totalCash = entries.reduce(
    (sum, item) =>
      sum +
      ((item.registrationFeeMode === "Cash"
        ? Number(item.registrationFee)
        : 0) || 0) +
      ((item.testFeeMode === "Cash" ? Number(item.testFee) : 0) || 0),
    0
  );

  const totalRefund = entries.reduce(
    (sum, item) =>
      sum +
      ((item.status === "Refund"
        ? Number(item.registrationFee) + Number(item.testFee)
        : 0) || 0),
    0
  );

  const totalOnline = entries.reduce(
    (sum, item) =>
      sum +
      ((item.registrationFeeMode !== "Cash"
        ? Number(item.registrationFee)
        : 0) || 0) +
      ((item.testFeeMode !== "Cash" ? Number(item.testFee) : 0) || 0),
    0
  );

  const grandTotal = totalRegistration + totalTestFee;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <ExportExcel
          tableId="daily-collection-report-table"
          filename={`daily-collection-report-${fromDate}-to-${toDate}.csv`}
        />
      </div>
      <Table
        striped
        bordered
        hover
        id="daily-collection-report-table"
        ref={tableRef}
      >
        <thead>
          <tr>
            <th>SL NO.</th>
            <th>PRESCRIPTION</th>
            <th>EDIT</th>
            <th>PATIENT NAME</th>
            <th>DOCTOR NAME</th>
            <th>VISIT DATE</th>
            <th>REGISTRATION FEE</th>
            <th>TEST DATE</th>
            <th>TEST FEE</th>
            <th>TOTAL</th>
            <th>REMARKS</th>
          </tr>
        </thead>
        <tbody>
          {entries.length > 0 ? (
            <>
              {entries.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    <Button
                      variant="primary"
                      onClick={() => printPrescription(item.patientId)}
                    >
                      PRESCRIPTION
                    </Button>
                  </td>
                  <td>
                    <Button
                      variant="warning"
                      onClick={() => editDetails(item.patientId)}
                    >
                      EDIT
                    </Button>
                  </td>
                  <td>{item.patientName}</td>
                  <td>{item.doctorName}</td>
                  <td>
                    {item.visitDate !== null &&
                      item.visitDate
                        .split(" ")[0]
                        .split("-")
                        .reverse()
                        .join("/")}
                  </td>
                  <td>{item.registrationFee}</td>
                  <td>
                    {item.testDate !== null &&
                      item.testDate
                        .split(" ")[0]
                        .split("-")
                        .reverse()
                        .join("/")}
                  </td>
                  <td>{item.testFee || 0}</td>
                  <td>
                    {(Number(item.registrationFee) || 0) +
                      (Number(item.testFee) || 0)}
                  </td>
                  <td className="fw-bold">
                    {item.status === "Refund" && "REFUND"}
                  </td>
                </tr>
              ))}

              {/* ✅ Total Row */}
              <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                <td colSpan="6" className="text-end">
                  TOTAL:
                </td>
                <td>{totalRegistration}</td>
                <td></td>
                <td>{totalTestFee}</td>
                <td>{grandTotal}</td>
                <td></td>
              </tr>

              <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                <td colSpan="6" className="text-end">
                  CASH:
                </td>
                <td colSpan="4">{totalCash}</td>
                <td></td>
              </tr>

              <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                <td colSpan="6" className="text-end">
                  ONLINE:
                </td>
                <td colSpan="4">{totalOnline}</td>
                <td></td>
              </tr>

              <tr
                style={{
                  fontWeight: "bold",
                  background: "#f0f0f0",
                }}
              >
                <td colSpan="6" className="text-end" style={{ color: "red" }}>
                  REFUND:
                </td>
                <td colSpan="4" style={{ textAlign: "right", color: "red" }}>
                  {totalRefund}
                </td>
                <td></td>
              </tr>

              <tr style={{ fontWeight: "bold", background: "#f0f0f0" }}>
                <td colSpan="6" className="text-end">
                  NET TOTAL:
                </td>
                <td colSpan="4" style={{ textAlign: "right" }}>
                  {grandTotal - totalRefund}
                </td>
                <td></td>
              </tr>
            </>
          ) : (
            <tr>
              <td colSpan="11" className="text-center">
                No Entries
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </>
  );
}

export default DailyCollectionReportTable;
